import React, { useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Switch,
    Tooltip,
    Typography,
    CircularProgress,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { Add, Delete, Edit } from '@material-ui/icons'

import { RenewalSequence } from '../../../models/Concession'
import { useRenewalSequences } from '../../../hooks/useRenewalSequences'
import { SequenceEditorModal } from './SequenceEditorModal'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    card: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: theme.shadows[2],
        },
    },
    cardContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    cardInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    cardActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    stepChip: {
        height: 22,
        fontSize: '0.6875rem',
        fontWeight: 600,
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6, 2),
    },
}))

interface SequencesTabProps {
    readOnly?: boolean
    leasingSeasonId?: number
}

export const SequencesTab: React.FC<SequencesTabProps> = ({
    readOnly = false,
    leasingSeasonId,
}) => {
    const classes = useStyles()
    const {
        sequences,
        isLoading,
        patchSequence,
        deleteSequence,
    } = useRenewalSequences()

    const [editorOpen, setEditorOpen] = useState(false)
    const [editingSequence, setEditingSequence] = useState<
        RenewalSequence | undefined
    >()
    const [confirmDelete, setConfirmDelete] = useState<RenewalSequence | null>(
        null,
    )
    const [confirmActivate, setConfirmActivate] = useState<{
        sequence: RenewalSequence
        activate: boolean
    } | null>(null)

    const handleEdit = (seq: RenewalSequence) => {
        setEditingSequence(seq)
        setEditorOpen(true)
    }

    const handleNew = () => {
        setEditingSequence(undefined)
        setEditorOpen(true)
    }

    const handleToggleActive = (seq: RenewalSequence) => {
        if (!seq.is_active) {
            const currentActive = sequences.find(
                (s) => s.is_active && s.id !== seq.id,
            )
            if (currentActive) {
                setConfirmActivate({ sequence: seq, activate: true })
                return
            }
        }
        patchSequence.mutate({
            id: seq.id,
            body: { is_active: !seq.is_active },
        })
    }

    const handleConfirmActivate = () => {
        if (!confirmActivate) return
        const currentActive = sequences.find(
            (s) => s.is_active && s.id !== confirmActivate.sequence.id,
        )
        if (currentActive) {
            patchSequence.mutate(
                { id: currentActive.id, body: { is_active: false } },
                {
                    onSuccess: () => {
                        patchSequence.mutate({
                            id: confirmActivate.sequence.id,
                            body: { is_active: true },
                        })
                    },
                },
            )
        } else {
            patchSequence.mutate({
                id: confirmActivate.sequence.id,
                body: { is_active: true },
            })
        }
        setConfirmActivate(null)
    }

    const handleDeleteConfirm = () => {
        if (!confirmDelete) return
        deleteSequence.mutate(confirmDelete.id)
        setConfirmDelete(null)
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <Box>
            <Box className={classes.header}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Renewal Sequences
                </Typography>
                {!readOnly && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleNew}
                        style={{ textTransform: 'none' }}
                    >
                        New Sequence
                    </Button>
                )}
            </Box>

            {sequences.length === 0 ? (
                <Box className={classes.emptyState}>
                    <Box display="flex" justifyContent="center" mb={2}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                backgroundColor: '#6570E610',
                            }}
                        >
                            <Add
                                style={{
                                    color: '#6570E6',
                                    fontSize: 28,
                                }}
                            />
                        </Box>
                    </Box>
                    <Typography
                        variant="body1"
                        style={{ fontWeight: 600, marginBottom: 4 }}
                    >
                        No sequences yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Create a sequence to automate renewal communications
                        with your tenants.
                    </Typography>
                </Box>
            ) : (
                sequences.map((seq) => (
                    <Card
                        key={seq.id}
                        className={classes.card}
                        variant="outlined"
                    >
                        <CardContent className={classes.cardContent}>
                            <Box className={classes.cardInfo}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gridGap={8}
                                >
                                    <Typography
                                        variant="body1"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {seq.name}
                                    </Typography>
                                    {seq.is_active && (
                                        <Chip
                                            label="Active"
                                            size="small"
                                            className={classes.stepChip}
                                            style={{
                                                backgroundColor: '#d1fae5',
                                                color: '#047857',
                                            }}
                                        />
                                    )}
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    {seq.steps.length} step
                                    {seq.steps.length === 1 ? '' : 's'}
                                    {seq.steps.length > 0 && ' \u2014 '}
                                    {seq.steps.length > 0 &&
                                        (() => {
                                            const counts: Record<
                                                string,
                                                number
                                            > = {}
                                            seq.steps.forEach((s) => {
                                                counts[s.step_type] =
                                                    (counts[s.step_type] || 0) +
                                                    1
                                            })
                                            return Object.entries(counts)
                                                .map(
                                                    ([type, count]) =>
                                                        `${count} ${type.toLowerCase()}`,
                                                )
                                                .join(', ')
                                        })()}
                                    {seq.leasing_season_name &&
                                        ` \u00B7 ${seq.leasing_season_name}`}
                                </Typography>
                            </Box>
                            {!readOnly && (
                                <Box className={classes.cardActions}>
                                    <Tooltip
                                        title={
                                            seq.is_active
                                                ? 'Deactivate'
                                                : 'Activate'
                                        }
                                    >
                                        <Switch
                                            checked={seq.is_active}
                                            onChange={() =>
                                                handleToggleActive(seq)
                                            }
                                            color="primary"
                                            size="small"
                                        />
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(seq)}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setConfirmDelete(seq)
                                            }
                                        >
                                            <Delete
                                                fontSize="small"
                                                color="error"
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                ))
            )}

            <SequenceEditorModal
                open={editorOpen}
                sequence={editingSequence}
                leasingSeasonId={leasingSeasonId}
                onClose={() => {
                    setEditorOpen(false)
                    setEditingSequence(undefined)
                }}
            />

            {/* Activate confirmation */}
            <Dialog
                open={confirmActivate !== null}
                onClose={() => setConfirmActivate(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: { borderRadius: 12, padding: 8 },
                }}
            >
                {confirmActivate && (
                    <>
                        <DialogTitle
                            disableTypography
                            style={{ textAlign: 'center', paddingBottom: 0 }}
                        >
                            <Typography
                                variant="h6"
                                style={{
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                }}
                            >
                                Activate sequence
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{
                                    textAlign: 'center',
                                    lineHeight: 1.6,
                                }}
                            >
                                Only one sequence can be active at a time.
                                Activating &ldquo;
                                {confirmActivate.sequence.name}&rdquo; will
                                first deactivate the current active sequence.
                                Continue?
                            </Typography>
                        </DialogContent>
                        <DialogActions
                            style={{ padding: '8px 24px 20px', gap: 8 }}
                        >
                            <Button
                                onClick={() => setConfirmActivate(null)}
                                variant="outlined"
                                fullWidth
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleConfirmActivate}
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    boxShadow: 'none',
                                }}
                            >
                                Activate
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Delete confirmation */}
            <Dialog
                open={confirmDelete !== null}
                onClose={() => setConfirmDelete(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: { borderRadius: 12, padding: 8 },
                }}
            >
                {confirmDelete && (
                    <>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            pt={3}
                            pb={1}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    backgroundColor: '#E2563B15',
                                }}
                            >
                                <Delete style={{ color: '#E2563B' }} />
                            </Box>
                        </Box>
                        <DialogTitle
                            disableTypography
                            style={{ textAlign: 'center', paddingBottom: 0 }}
                        >
                            <Typography
                                variant="h6"
                                style={{
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                }}
                            >
                                Delete sequence
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{
                                    textAlign: 'center',
                                    lineHeight: 1.6,
                                }}
                            >
                                Permanently delete &ldquo;{confirmDelete.name}
                                &rdquo;? This action cannot be undone.
                            </Typography>
                        </DialogContent>
                        <DialogActions
                            style={{ padding: '8px 24px 20px', gap: 8 }}
                        >
                            <Button
                                onClick={() => setConfirmDelete(null)}
                                variant="outlined"
                                fullWidth
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleDeleteConfirm}
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    color: '#fff',
                                    backgroundColor: '#E2563B',
                                    boxShadow: 'none',
                                }}
                            >
                                Delete
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    )
}
